import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  AfterViewChecked,
  OnChanges,
  OnInit,
  OnDestroy,
} from '@angular/core';

import { UntypedFormGroup } from '@angular/forms';
import {
  SelectOutput,
  KeyUpOutput,
} from '../../interfaces/input-autocomplete.interface';
import { Subject } from 'rxjs';
import { debounceTime, filter, tap } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ValidationError } from '../../interfaces/validation-error.interface';
import { AutocompleteDefinition } from '../../interfaces/form-controls-definition.interface';
import { FormBuilderLabel } from '../../interfaces/formData.interface';
import { Hint } from '../hint/hint.component';

interface FilteredResults {
  nomatch?: boolean;
  focused?: boolean;
  value?: string;
}

@UntilDestroy()
@Component({
  selector: 'dg-fb-input-autocomplete',
  templateUrl: './input-autocomplete.component.html',
  styleUrls: ['./input-autocomplete.component.scss'],
})
export class InputAutoCompleteComponent
  implements OnInit, AfterViewChecked, OnChanges, OnDestroy
{
  constructor(private cdRef: ChangeDetectorRef) {}

  @Input() group?: UntypedFormGroup;
  @Input() item?: AutocompleteDefinition['autocomplete'];
  @Input() maxLength: number | null = null;
  @Input() label?: FormBuilderLabel;
  @Input() hint?: Hint;
  @Input() labelFor?: string;
  @Input() showValidation = false;
  @Input() validators?: ValidationError[];
  @Input() results: string[] | null = null;
  @Input() triggerLookUpCheck = false;
  @Input() startSearchFromCharacter = 1;

  @Output() selectedItem: EventEmitter<SelectOutput> = new EventEmitter();
  @Output() keyUp: EventEmitter<KeyUpOutput> = new EventEmitter();
  @Output() keyUpDebounced: EventEmitter<KeyUpOutput> = new EventEmitter();

  public lookupMatch = false;
  public isMousedown = false;
  public userEntered = '';
  public filtered: FilteredResults[] = [];
  public found?: string[] = []; // holds filtered data but not HTML formatted.
  public isTyping = false;
  public supressKeyUp = false;
  public selected?: string;
  public index = 0;
  public isValid = false;
  public state: string | null = null;
  public keyUpSubject$: Subject<KeyUpOutput> = new Subject();
  private searchStarted = false;

  /**
   * ChangeDetectorRef is using to prevent angular exception error
   * Expression has changed after it was checked. Previous value: xxx. Current value: yyy
   * DO NOT DELETE OR MODIFY THIS CODE!!!
   */
  ngAfterViewChecked() {
    if (this.state !== null) {
      this.setErrorState(this.state);
    }
    this.cdRef.detectChanges();
  }

  ngOnInit() {
    this.userEntered = this.group?.controls[this.item?.control as string].value;

    this.keyUpSubject$
      .pipe(
        untilDestroyed(this),
        debounceTime(400),
        tap((realTime: KeyUpOutput) => {
          this.keyUp.emit(realTime);
          this.filterResults(this.results as string[]);
        }),
        filter(
          (realTime: KeyUpOutput) =>
            realTime.q.length >= this.startSearchFromCharacter
        )
      )
      .subscribe((debounced: KeyUpOutput) =>
        this.keyUpDebounced.emit(debounced)
      );
  }

  ngOnChanges() {
    this.waitResults();
  }
  /**
   * waitResults
   * it takes sometime to bind results of search api from parent component
   * we have to wait results before call filterResults method
   */
  waitResults() {
    if (this.results !== undefined && this.isTyping) {
      this.filterResults(this.results as string[]);
      if (this.userEntered !== '' || this.userEntered !== undefined) {
        this.lookupMatch = false;
      } else {
        this.lookupMatch = true;
      }
    }
  }

  /**
   * filterResults
   * @param results[]
   * filtering results from API and dynamically highlighting user typed in blue
   * setting first matched element of drop down to hover state
   */
  filterResults(results: string[]) {
    this.filtered = [];
    this.found = [];

    if (
      results &&
      this.userEntered !== undefined &&
      this.userEntered !== null &&
      (this.userEntered.length >= this.startSearchFromCharacter ||
        this.searchStarted)
    ) {
      if (this.userEntered) {
        for (let i = 0; i < results.length; i++) {
          // if substring (case insensitive) matching show results
          const regex = new RegExp(this.userEntered, 'gi');
          const match = results[i].match(regex);
          if (match) {
            this.filtered.push({
              focused: false,
              value: results[i].replace(
                regex,
                `<strong class="autocomplete__highlight">${match[0]}</strong>`
              ),
            });

            this.found.push(results[i] as string);
          }
        }
      }

      // if there is no direct match
      // When user typing without spaces
      // We should not highlight text user typing
      if (this.found.length >= 1 && this.filtered.length === 0) {
        for (let i = 0; i < results.length; i++) {
          this.filtered.push({
            focused: false,
            value: results[i],
          });
        }
      }

      if (this.found.length === 0 && this.userEntered.length > 0) {
        this.filtered.push({
          nomatch: true,
          focused: false,
          value: 'No match found',
        });
      }

      if (this.userEntered.length < this.startSearchFromCharacter) {
        this.filtered = [];
      }

      this.searchStarted = true;

      setTimeout(() => {
        this.index = 0;
        this.focusResult(this.index);
      }); // push to end of stack
    } else {
      // No match found error
      this.lookUpCheck();
    }
  }

  /**
   * focusResult
   * @param index
   * simulate onHover state by adding flag to selected element
   */
  public focusResult(index: number): void {
    this.filtered.map((el) => {
      if (el.focused === true) {
        el.focused = false;
      }
    });
    if (this.filtered[index]) {
      this.filtered[index].focused = true;
    }
  }

  /**
   * selectResult
   * @param index
   * filter data from html tags and emits selected data to parent component
   * re-validates user entered data again
   * re-set all searched settings to initial state
   */
  public selectResult(index: number): void {
    if (this.filtered[0].nomatch !== true) {
      this.resetErrorState();
      const openTag = new RegExp(
        '<strong class="autocomplete__highlight">',
        'g'
      );
      const closeTag = new RegExp('</strong>', 'g');
      let selected;
      if (this.filtered[index]) {
        selected = this.filtered[index]?.value
          ?.replace(openTag, '')
          ?.replace(closeTag, '');
      }
      this.isTyping = false;
      this.lookupMatch = true;
      this.state = null;
      this.isValid = true;
      this.group?.controls[this.item?.control as string].patchValue(selected);
      this.selectedItem.emit({
        q: selected as string,
        api: this.item?.control as string,
      });
    }
  }

  /**
   * lookUpCheck
   * manage validators required and invalid based on user entered and filtered results
   */
  public lookUpCheck(): boolean | void {
    this.userEntered = this.group?.controls[this.item?.control as string].value;

    if (this.isValid === true) {
      this.lookupMatch = true;
    }

    const setErrors = () => {
      this.setErrorState(this.state as string);
      this.lookupMatch = true;
    };

    if (
      this.results?.find(
        (r) => r?.toUpperCase() === this.userEntered?.toUpperCase()
      ) !== undefined
    ) {
      this.group?.controls[this.item?.control as string].patchValue(
        this.userEntered.toUpperCase()
      );
      this.isValid = true;
      this.state = null;
      setErrors();
      return false;
    }

    if (this.userEntered === undefined || this.userEntered === '') {
      this.state = 'required';
      setErrors();
      return false;
    }
    if (
      this.isValid !== undefined &&
      (this.found as string[]).length === 0 &&
      (this.userEntered !== undefined || this.userEntered !== '')
    ) {
      this.state = 'invalid';
      setErrors();
      return false;
    }
    // For strict match
    if (
      this.filtered.length === 1 &&
      this.userEntered === (this.found as string[])[0]
    ) {
      this.isValid = true;
      this.state = null;
      setErrors();
      return false;
    }
    // For strict match uppercase matching
    if (
      this.filtered.length === 1 &&
      this.userEntered.toUpperCase() === (this.found as string[])[0]
    ) {
      this.group?.controls[this.item?.control as string].patchValue(
        this.userEntered.toUpperCase()
      );
      this.isValid = true;
      this.state = null;
      setErrors();
      return false;
    }
    if (
      this.isValid !== true &&
      this.filtered.length >= 1 &&
      this.lookupMatch === true &&
      this.isTyping === false
    ) {
      this.state = 'invalid';
      setErrors();
      return false;
    }
  }

  /**
   * typeAhead on KeyDown event
   * @param event
   * enabled keyboard interaction with filtered results navigation and selection for accessibility
   * manage hover state coupled to cursor key selection
   */
  public typeAhead(event: Event, index?: number): void {
    this.supressKeyUp = false;
    if (
      (event as KeyboardEvent).key === 'ArrowLeft' ||
      (event as KeyboardEvent).key === 'Left'
    ) {
      this.supressKeyUp = true;
      return;
    }
    if (
      (event as KeyboardEvent).key === 'ArrowRight' ||
      (event as KeyboardEvent).key === 'Right'
    ) {
      this.supressKeyUp = true;
      return;
    }
    if (
      (event as KeyboardEvent).key === 'ArrowDown' ||
      (event as KeyboardEvent).key === 'Down'
    ) {
      this.supressKeyUp = true;
      if (this.index < this.filtered.length - 1) {
        this.index++;
        this.focusResult(this.index);
        return;
      }
    }
    if (
      (event as KeyboardEvent).key === 'ArrowUp' ||
      (event as KeyboardEvent).key === 'Up'
    ) {
      this.supressKeyUp = true;
      if (this.index >= 1) {
        this.index--;
        this.focusResult(this.index);
        return;
      }
    }
    if ((event as KeyboardEvent).key === 'Enter') {
      this.selectResult(this.index);
      this.supressKeyUp = true;
      return;
    }
    if (event.type === 'mouseenter') {
      this.supressKeyUp = true;
      this.index = index as number;
      this.focusResult(this.index);
    }

    if ((event as KeyboardEvent).key === 'Backspace') {
      if (this.searchStarted && this.userEntered.length === 1) {
        // reset on delete last char
        this.userEntered = '';
        this.filterResults([]);
      }
    }
  }

  /**
   * inputKeyUp on KeyUp event
   * passing user entered data to parent component which is calling the search api
   */
  public inputKeyUp() {
    if (this.supressKeyUp === false) {
      this.userEntered =
        this.group?.controls[this.item?.control as string].value;

      this.isValid = false;

      // Put fix here for too many requests issue DGX-1962
      if (this.userEntered) {
        const keyUpOutput: KeyUpOutput = {
          q: this.userEntered,
          category: this.item?.DataCategory as string,
          type: this.item?.DataType as string,
        };
        this.keyUpSubject$.next(keyUpOutput);
      }
    }
  }

  /**
   * resetErrorState
   * @param state
   * set validation errors
   */
  public resetErrorState() {
    this.group?.get(this.item?.control as string)?.setErrors(null);
  }
  /**
   * setErrorState
   * @param state
   * set validation errors
   */
  public setErrorState(state: string) {
    this.resetErrorState();
    this.showValidation = true;
    // No match found error
    if (state === 'invalid') {
      this.group?.get(this.item?.control as string)?.setErrors({
        invalid: true,
      });
    }
    // field is required error
    if (state === 'required') {
      this.group?.get(this.item?.control as string)?.setErrors({
        required: true,
      });
    }
  }

  /**
   * handleOnBlur
   */
  public handleOnBlur() {
    if (this.isMousedown) {
      return;
    }

    this.isTyping = false;
    this.lookupMatch = true;

    if (this.isValid !== true) {
      this.lookUpCheck();
    }
  }

  /**
   * handleOnFocus
   */
  handleOnFocus() {
    this.isTyping = true;
    this.lookupMatch = false;
    this.group?.controls[this.item?.control as string].markAsUntouched();
  }

  /**
   * clearInput
   * mousedown event called before blur set to true to stop handleOnBlur() being called while resetting input
   */
  clearInput() {
    this.isMousedown = true;
    this.group?.controls[this.item?.control as string].patchValue('');
    this.group?.controls[this.item?.control as string].markAsUntouched();
    this.isMousedown = false;
    this.filtered = [];
  }

  ngOnDestroy() {
    // method required for ngx-take-until-destroy
  }
}
